import { chapterIndexByBlock } from './tokenizer.js';
import { tokenIntervalMs } from './timing.js';
import { buildChunkTable, stickSets } from './chunking.js';
/** Product WPM range (PRD §1); engine clamps rather than trusting shells. */
export const WPM_RANGE = { min: 100, max: 1200 };
/**
 * Session milestone spacing in words (one prototype "page" = 250 words).
 * The engine only emits the event; milestone notifications are parked (§6).
 */
export const DEFAULT_MILESTONE_WORDS = 250;
export const DEFAULT_WPM_SAMPLE_MS = 5000;
/**
 * RSVP playback engine. Deterministic under an injected Clock (B-2): shells
 * call tick() on their own cadence (rAF or otherwise) and the fixed-timestep
 * accumulator advances words, carrying the sub-frame remainder so no interval
 * is ever silently dropped. Session accounting (words, activeMs, wpm samples)
 * lives here, not in shells (PD-7).
 */
export class Engine {
    doc;
    tokens;
    clock;
    adaptiveEnabled;
    warmup;
    sticks;
    chapterByBlock;
    milestoneEveryWords;
    wpmSampleEveryMs;
    maxWpm;
    wpm;
    chunkSize;
    entries;
    entryIdx = 0;
    playing = false;
    finished;
    startedOnce = false;
    lastTickMs;
    accumulatorMs = 0;
    currentIntervalMs;
    wordsRead = 0;
    activeMs = 0;
    wpmSamples = [];
    nextSampleAtMs;
    nextMilestoneAtWords;
    listeners = [];
    constructor(options) {
        this.doc = options.doc;
        this.tokens = options.tokens;
        this.clock = options.clock;
        this.adaptiveEnabled = options.adaptiveEnabled ?? true;
        this.warmup = options.warmup;
        this.sticks = stickSets(options.langPack, this.doc.lang);
        this.chapterByBlock = chapterIndexByBlock(this.doc.blocks);
        this.milestoneEveryWords = options.milestoneEveryWords ?? DEFAULT_MILESTONE_WORDS;
        this.wpmSampleEveryMs = options.wpmSampleEveryMs ?? DEFAULT_WPM_SAMPLE_MS;
        this.maxWpm = Math.max(WPM_RANGE.min, options.maxWpm ?? WPM_RANGE.max);
        this.wpm = this.clampWpm(options.wpm);
        this.chunkSize = options.chunkSize ?? 1;
        this.entries = buildChunkTable(this.tokens, this.chunkSize, this.sticks, this.doc.lang);
        this.finished = this.entries.length === 0;
        this.nextSampleAtMs = this.wpmSampleEveryMs;
        this.nextMilestoneAtWords = this.milestoneEveryWords;
    }
    on(listener) {
        this.listeners.push(listener);
        return () => {
            this.listeners = this.listeners.filter((l) => l !== listener);
        };
    }
    play() {
        if (this.playing || this.finished)
            return;
        const now = this.clock.now();
        if (!this.startedOnce) {
            this.startedOnce = true;
            this.warmup?.start(now);
            this.emitWordChanged();
        }
        else {
            // M-05: when the resume changes the rate modifier (mini/full warm-up
            // restart), the interval cached before the pause is priced at the wrong
            // multiplier — invalidate it, and drop the pre-pause sub-interval carry:
            // a restarted ramp begins clean, so the FIRST post-resume word is
            // consumed at the ramp's pace, never early off stale time. A <10 s
            // continue leaves multiplier, cache and carry untouched, exactly as
            // before.
            const before = this.warmup?.multiplierAt(now) ?? 1;
            this.warmup?.noteResume(now);
            if ((this.warmup?.multiplierAt(now) ?? 1) !== before) {
                this.currentIntervalMs = undefined;
                this.accumulatorMs = 0;
            }
        }
        this.playing = true;
        this.lastTickMs = now;
    }
    pause() {
        if (!this.playing)
            return;
        const now = this.clock.now();
        this.accumulateActive(now);
        this.playing = false;
        this.warmup?.notePause(now);
    }
    /**
     * Advance playback to the clock's current time. Consumes as many whole
     * intervals as the accumulator holds (a long tick advances several words);
     * the remainder is carried, never dropped.
     */
    tick() {
        if (!this.playing)
            return;
        const now = this.clock.now();
        this.accumulateActive(now);
        let interval = this.currentIntervalMs ?? this.computeIntervalMs(now);
        this.currentIntervalMs = interval;
        // M-05: while the rate modifier is ramping, the interval is time-dependent —
        // a burst of buffered time (a shell that stopped ticking without pausing)
        // must not replay many virtual ticks all priced at this single frame's
        // multiplier. Detect a ramp by comparing the multiplier across the buffered
        // window; when ramping, clamp a pathological backlog to one interval and
        // consume at most one entry per frame, so every further word requires a
        // fresh frame (and a fresh multiplier). With no active ramp the multiplier
        // is time-invariant and catch-up consumption is untouched (B-2 carry).
        const ramping = this.warmup !== undefined &&
            this.warmup.multiplierAt(now - this.accumulatorMs) !== this.warmup.multiplierAt(now);
        if (ramping && this.accumulatorMs > 2 * interval)
            this.accumulatorMs = interval;
        while (this.playing && this.accumulatorMs >= interval) {
            this.accumulatorMs -= interval;
            this.advance();
            if (!this.playing)
                break;
            interval = this.computeIntervalMs(now);
            this.currentIntervalMs = interval;
            if (ramping)
                break;
        }
    }
    /** Reposition to the entry containing tokenIdx. Does not touch accounting. */
    seek(tokenIdx) {
        if (this.entries.length === 0)
            return;
        const clamped = Math.max(0, Math.min(this.tokens.length - 1, tokenIdx));
        const fromChapter = this.currentChapterIdx();
        this.entryIdx = this.entryIndexForToken(clamped);
        this.finished = false;
        this.accumulatorMs = 0;
        this.currentIntervalMs = undefined;
        const toChapter = this.currentChapterIdx();
        if (toChapter !== fromChapter) {
            const direction = toChapter < fromChapter ? 'backward' : 'forward';
            this.emit({ type: 'chapterEntered', chapterIdx: toChapter, direction });
        }
        this.emitWordChanged();
    }
    /** Manual speed change: applies immediately and cancels warm-up. */
    setWpm(wpm) {
        this.wpm = this.clampWpm(wpm);
        this.warmup?.cancel();
        this.currentIntervalMs = undefined;
    }
    clampWpm(wpm) {
        return Math.min(this.maxWpm, Math.max(WPM_RANGE.min, wpm));
    }
    getWpm() {
        return this.wpm;
    }
    setChunkSize(chunkSize) {
        if (chunkSize === this.chunkSize)
            return;
        const currentToken = this.entries[this.entryIdx]?.start ?? 0;
        this.chunkSize = chunkSize;
        this.entries = buildChunkTable(this.tokens, chunkSize, this.sticks, this.doc.lang);
        this.entryIdx = this.entryIndexForToken(currentToken);
        this.accumulatorMs = 0;
        this.currentIntervalMs = undefined;
        this.emitWordChanged();
    }
    snapshot() {
        return {
            tokenIdx: this.entries[this.entryIdx]?.start ?? 0,
            entryIdx: this.entryIdx,
            playing: this.playing,
            finished: this.finished,
            wordsRead: this.wordsRead,
            activeMs: this.activeMs,
            chapterIdx: this.currentChapterIdx(),
            wpmSamples: [...this.wpmSamples],
        };
    }
    accumulateActive(nowMs) {
        const last = this.lastTickMs ?? nowMs;
        const delta = Math.max(0, nowMs - last);
        this.lastTickMs = nowMs;
        this.activeMs += delta;
        this.accumulatorMs += delta;
        while (this.activeMs >= this.nextSampleAtMs) {
            this.wpmSamples.push({
                activeMs: this.nextSampleAtMs,
                wpm: Math.round((this.wordsRead * 60000) / this.nextSampleAtMs),
            });
            this.nextSampleAtMs += this.wpmSampleEveryMs;
        }
    }
    advance() {
        const entry = this.entries[this.entryIdx];
        if (entry === undefined)
            return;
        this.wordsRead += entry.count;
        while (this.wordsRead >= this.nextMilestoneAtWords) {
            this.emit({ type: 'milestone', words: this.nextMilestoneAtWords });
            this.nextMilestoneAtWords += this.milestoneEveryWords;
        }
        if (this.entryIdx === this.entries.length - 1) {
            this.finished = true;
            this.playing = false;
            this.emit({ type: 'finished' });
            return;
        }
        const fromChapter = this.currentChapterIdx();
        this.entryIdx += 1;
        const toChapter = this.currentChapterIdx();
        if (toChapter !== fromChapter) {
            this.emit({ type: 'chapterEntered', chapterIdx: toChapter, direction: 'forward' });
        }
        this.emitWordChanged();
    }
    computeIntervalMs(nowMs) {
        const entry = this.entries[this.entryIdx];
        if (entry === undefined)
            return Number.POSITIVE_INFINITY;
        const effectiveWpm = this.wpm * (this.warmup?.multiplierAt(nowMs) ?? 1.0);
        let sum = 0;
        for (let i = entry.start; i < entry.start + entry.count; i++) {
            const token = this.tokens[i];
            if (token !== undefined) {
                sum += tokenIntervalMs(token, effectiveWpm, this.adaptiveEnabled);
            }
        }
        return sum;
    }
    entryIndexForToken(tokenIdx) {
        for (let i = this.entries.length - 1; i >= 0; i--) {
            const entry = this.entries[i];
            if (entry !== undefined && entry.start <= tokenIdx)
                return i;
        }
        return 0;
    }
    currentChapterIdx() {
        const entry = this.entries[this.entryIdx];
        const token = entry === undefined ? undefined : this.tokens[entry.start];
        if (token === undefined)
            return 0;
        return this.chapterByBlock[token.blockIdx] ?? 0;
    }
    emitWordChanged() {
        const entry = this.entries[this.entryIdx];
        if (entry === undefined)
            return;
        this.emit({ type: 'wordChanged', tokenIdx: entry.start, tokenCount: entry.count });
    }
    emit(event) {
        for (const listener of [...this.listeners])
            listener(event);
    }
}
//# sourceMappingURL=engine.js.map